import { ReactNode } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import ChangeLine from '../../../components/novarium/ChangeLine'
import {
  OperatingRoomTimeMeasureDTO,
  OperatingRoomTimeRowDTO,
  OperatingRoomTimesDTO,
} from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { pointsVariation, Variation } from '../../ServicesStatistics/statisticsChange'
import { SectionState } from '../../UrgencySummary/useSectionData'
import { OperatingRoomDrill } from '../operatingRoomFilterModel'
import {
  formatCount,
  formatDuration,
  formatPercent,
  formatSignedDuration,
  NO_VALUE,
} from '../operatingRoomFormat'
import { absoluteVariation, previousPeriodRange, toRatio } from '../operatingRoomVariation'
import Indicator from './OperatingRoomIndicatorCard'
import OperatingRoomSection from './OperatingRoomSection'
import OperatingRoomTable, { OperatingRoomTableColumn } from './OperatingRoomTable'

/** «Con 4 cirugías sin horas válidas: …»: una frase más de la ⓘ, solo si hay (N > 0). */
function noteIf(intl: IntlShape, id: string, count: number): string {
  return count > 0 ? ` ${intl.formatMessage({ id }, { count, countText: formatCount(intl, count) })}` : ''
}

/** «15 min tarde», «3 min antes» o «0 min»: cuánto se corrió el inicio respecto del turno (la mediana puede ser negativa). */
function formatStartDelay(intl: IntlShape, minutes: number | null): string {
  if (minutes === null) return NO_VALUE
  const rounded = Math.round(minutes)
  if (rounded === 0) return formatDuration(intl, 0)
  return intl.formatMessage(
    { id: rounded > 0 ? 'OperatingRoomIndicatorsTimesLateValue' : 'OperatingRoomIndicatorsTimesEarlyValue' },
    { value: formatDuration(intl, Math.abs(rounded)) },
  )
}

interface OperatingRoomTimesProps {
  times: SectionState<OperatingRoomTimesDTO>
  /** Una fila abre la lista con las cirugías realizadas de ese tipo o práctica; el total de la lista es el de la fila. */
  onDrill: (drill: OperatingRoomDrill) => void
}

/**
 * Pestaña «Tiempos»: puntualidad de inicio, espera hasta el inicio, duración real, real menos programada y recambio entre
 * cirugías, cada uno con su variación contra el período anterior (neutra: sin «mejor» ni «peor»), con la base de casos y la
 * fórmula a demanda en su ⓘ. Debajo, las medidas por tipo de cirugía y por práctica. El total de cirugías de cada fila abre la
 * lista; las medidas que excluyen casos no tienen un filtro propio en la lista y no abren nada.
 */
export default function OperatingRoomTimes({ times, onDrill }: OperatingRoomTimesProps) {
  const intl = useIntl()
  const text = (id: string): string => intl.formatMessage({ id })

  return (
    <OperatingRoomSection
      label={text('OperatingRoomIndicatorsTabTimes')}
      state={times}
      errorMessageId="OperatingRoomIndicatorsTimesError"
    >
      {(data) => <TimesContent data={data} onDrill={onDrill} />}
    </OperatingRoomSection>
  )
}

function TimesContent({ data, onDrill }: { data: OperatingRoomTimesDTO; onDrill: (drill: OperatingRoomDrill) => void }) {
  const intl = useIntl()
  const { current, previous, previousFrom, previousTo } = data.summary
  const { dataNotes } = data
  const period = previousPeriodRange(previousFrom, previousTo)
  const text = (id: string): string => intl.formatMessage({ id })

  const line = (variation: Variation | null): ReactNode =>
    (variation && period ? <ChangeLine variation={variation} period={period} /> : undefined)
  const minutesLine = (now: number | null, before: number | null): ReactNode =>
    line(absoluteVariation(now, before, (difference) => formatDuration(intl, difference)))

  /** «p90 1 h 45 min · 405 casos» (el p90 y la base de la medida). */
  const spread = (measure: OperatingRoomTimeMeasureDTO, format: (minutes: number | null) => string = (m) => formatDuration(intl, m)): string =>
    intl.formatMessage({ id: measure.n === 0 ? 'OperatingRoomIndicatorsTimesCasesOnly' : 'OperatingRoomIndicatorsTimesP90Cases' }, {
      p90: format(measure.p90Minutes), count: measure.n, countText: formatCount(intl, measure.n),
    })

  const { startDelay, waiting, duration, realMinusScheduled, turnover } = current
  const before = previous

  const sharedNotes = noteIf(intl, 'OperatingRoomIndicatorsTimesNoteWithoutTimes', dataNotes.surgeriesWithoutValidTimes)

  const typeColumns = (name: (row: OperatingRoomTimeRowDTO) => string, nameHeaderId: string, drill: (row: OperatingRoomTimeRowDTO) => void) => {
    const columns: OperatingRoomTableColumn<OperatingRoomTimeRowDTO>[] = [
      {
        key: 'name',
        header: text(nameHeaderId),
        sortValue: (row) => name(row),
        cell: (row) => name(row),
      },
      {
        key: 'surgeries',
        width: 112,
        header: text('OperatingRoomIndicatorsTimesColSurgeries'),
        centered: true,
        sortValue: (row) => row.surgeries,
        cell: (row) => (row.id === null || row.surgeries === 0
          ? formatCount(intl, row.surgeries)
          : (
            <button
              type="button"
              className="nv-or-cell-drill"
              aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeriesOf' }, { name: name(row) })}
              onClick={() => drill(row)}
            >
              {formatCount(intl, row.surgeries)}
            </button>
          )),
      },
      {
        key: 'n',
        width: 104,
        header: text('OperatingRoomIndicatorsTimesColCases'),
        hint: text('OperatingRoomIndicatorsTimesColCasesHint'),
        centered: true,
        sortValue: (row) => row.n,
        cell: (row) => formatCount(intl, row.n),
      },
      {
        key: 'median',
        width: 112,
        header: text('OperatingRoomIndicatorsTimesColMedian'),
        centered: true,
        sortValue: (row) => row.medianMinutes,
        cell: (row) => formatDuration(intl, row.medianMinutes),
      },
      {
        key: 'p90',
        width: 112,
        header: text('OperatingRoomIndicatorsTimesColP90'),
        hint: text('OperatingRoomIndicatorsTimesColP90Hint'),
        centered: true,
        sortValue: (row) => row.p90Minutes,
        cell: (row) => formatDuration(intl, row.p90Minutes),
      },
      {
        key: 'scheduled',
        width: 152,
        header: text('OperatingRoomIndicatorsTimesColScheduled'),
        hint: text('OperatingRoomIndicatorsTimesColScheduledHint'),
        centered: true,
        sortValue: (row) => row.scheduledMedianMinutes,
        cell: (row) => formatDuration(intl, row.scheduledMedianMinutes),
      },
      {
        key: 'diff',
        width: 152,
        header: text('OperatingRoomIndicatorsTimesColDiff'),
        hint: text('OperatingRoomIndicatorsTimesColDiffHint'),
        centered: true,
        sortValue: (row) => row.diffMedianMinutes,
        cell: (row) => formatSignedDuration(intl, row.diffMedianMinutes),
      },
    ]
    return columns
  }

  const typeName = (row: OperatingRoomTimeRowDTO): string => (row.id === null ? text('OperatingRoomIndicatorsNoData') : row.name ?? NO_VALUE)
  const codeName = (row: OperatingRoomTimeRowDTO): string => (row.id === null
    ? text('OperatingRoomIndicatorsNoData')
    : [row.code, row.name].filter(Boolean).join(' · ') || NO_VALUE)

  return (
    <div className="nv-or-panel">
      <div className="nv-or-indicators-box">
        <div className="nv-or-indicators nv-or-indicators--six" role="group" aria-label={text('OperatingRoomIndicatorsTimesKpisLabel')}>
          <Indicator
            label={text('OperatingRoomIndicatorsTimesStart')}
            hint={text('OperatingRoomIndicatorsTimesStartHint')
              + noteIf(intl, 'OperatingRoomIndicatorsTimesNoteStartOutOfRange', dataNotes.surgeriesStartOutOfRange) + sharedNotes}
            value={formatStartDelay(intl, startDelay.medianMinutes)}
            details={[
              spread(startDelay, (minutes) => formatStartDelay(intl, minutes)),
              minutesLine(startDelay.medianMinutes, before.startDelay.medianMinutes),
            ]}
          />
          <Indicator
            label={intl.formatMessage({ id: 'OperatingRoomIndicatorsTimesLate' }, { minutes: startDelay.lateAfterMinutes })}
            hint={intl.formatMessage({ id: 'OperatingRoomIndicatorsTimesLateHint' }, { minutes: startDelay.lateAfterMinutes })}
            value={formatPercent(intl, startDelay.latePct)}
            details={[
              startDelay.n > 0
                ? intl.formatMessage({ id: 'OperatingRoomIndicatorsTimesLateOf' }, {
                  count: formatCount(intl, startDelay.lateCount), total: formatCount(intl, startDelay.n),
                })
                : undefined,
              line(pointsVariation(intl, toRatio(startDelay.latePct), toRatio(before.startDelay.latePct))),
            ]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsTimesWait')}
            hint={text('OperatingRoomIndicatorsTimesWaitHint')
              + noteIf(intl, 'OperatingRoomIndicatorsTimesNoteWithoutWait', dataNotes.surgeriesWithoutValidWait)}
            value={formatDuration(intl, waiting.medianMinutes)}
            details={[spread(waiting), minutesLine(waiting.medianMinutes, before.waiting.medianMinutes)]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsTimesDuration')}
            hint={text('OperatingRoomIndicatorsTimesDurationHint')
              + sharedNotes + noteIf(intl, 'OperatingRoomIndicatorsNoteTimedByQueue', dataNotes.surgeriesTimedByQueue)}
            value={formatDuration(intl, duration.medianMinutes)}
            details={[spread(duration), minutesLine(duration.medianMinutes, before.duration.medianMinutes)]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsTimesRealMinusScheduled')}
            hint={text('OperatingRoomIndicatorsTimesRealMinusScheduledHint')
              + noteIf(intl, 'OperatingRoomIndicatorsTimesNoteWithoutScheduled', dataNotes.surgeriesWithoutScheduledDuration)}
            value={formatSignedDuration(intl, realMinusScheduled.medianMinutes)}
            details={[
              intl.formatMessage({ id: realMinusScheduled.n === 0 ? 'OperatingRoomIndicatorsTimesCasesOnly' : 'OperatingRoomIndicatorsTimesRange' }, {
                p10: formatSignedDuration(intl, realMinusScheduled.p10Minutes),
                p90: formatSignedDuration(intl, realMinusScheduled.p90Minutes),
                count: realMinusScheduled.n,
                countText: formatCount(intl, realMinusScheduled.n),
              }),
              minutesLine(realMinusScheduled.medianMinutes, before.realMinusScheduled.medianMinutes),
            ]}
          />
          <Indicator
            label={text('OperatingRoomIndicatorsTimesTurnover')}
            hint={intl.formatMessage({ id: 'OperatingRoomIndicatorsTimesTurnoverHint' }, { minutes: turnover.maxGapMinutes })
              + noteIf(intl, 'OperatingRoomIndicatorsTimesNoteOverLimit', dataNotes.turnoverPairsOverLimit)}
            value={formatDuration(intl, turnover.medianMinutes)}
            details={[
              spread(turnover),
              turnover.pairs > 0
                ? intl.formatMessage({ id: 'OperatingRoomIndicatorsTimesOverlapping' }, { pct: formatPercent(intl, turnover.overlappingPct) })
                : undefined,
              minutesLine(turnover.medianMinutes, before.turnover.medianMinutes),
            ]}
          />
        </div>
      </div>

      <section className="nv-or-block" aria-labelledby="or-times-by-type">
        <h2 id="or-times-by-type" className="nv-section-title">{text('OperatingRoomIndicatorsTimesByType')}</h2>
        <OperatingRoomTable
          caption={text('OperatingRoomIndicatorsTimesByType')}
          rows={data.byType.rows}
          totalRows={data.byType.totalRows}
          rowKey={(row, index) => `${row.id ?? 'none'}-${index}`}
          columns={typeColumns(typeName, 'OperatingRoomIndicatorsColType', (row) => onDrill({ outcome: 'COMPLETED', patch: { surgeryTypeId: row.id } }))}
          defaultSort={{ key: 'surgeries', order: -1 }}
          emptyMessage={text('OperatingRoomIndicatorsTimesNoRows')}
        />
      </section>

      <section className="nv-or-block" aria-labelledby="or-times-by-code">
        <h2 id="or-times-by-code" className="nv-section-title">{text('OperatingRoomIndicatorsTimesByCode')}</h2>
        <OperatingRoomTable
          caption={text('OperatingRoomIndicatorsTimesByCode')}
          rows={data.byCode.rows}
          totalRows={data.byCode.totalRows}
          rowKey={(row, index) => `${row.id ?? 'none'}-${index}`}
          columns={typeColumns(codeName, 'OperatingRoomIndicatorsColCode', (row) => {
            if (row.id !== null) {
              onDrill({ outcome: 'COMPLETED', patch: { nomenclator: { id: row.id, name: [row.code, row.name].filter(Boolean).join(' - ') } } })
            }
          })}
          defaultSort={{ key: 'surgeries', order: -1 }}
          emptyMessage={text('OperatingRoomIndicatorsTimesNoRows')}
        />
      </section>
    </div>
  )
}
